import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  IChangePassword,
  ICodeLoginResponse,
  ICreateOrUpdateUserCredentials,
  ICreateUserResult,
  ILoginCredentials,
  ILoginResponse,
  IResetPasswordCredentials,
  IResetPasswordRequestCredentials,
} from "types/auth";
import * as amplitude from "@amplitude/analytics-browser";

import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  verifyTOTPSetup,
} from "aws-amplify/auth";

import { LocalStorage } from "utils/LocalStorage";
import { LoginResponseType, LoginSteps } from "helpers/constants";
import { ROLES, User } from "types/user";
import { generateTemporaryPassword } from "utils/generateTemporaryPassword";
import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";
import { normalizeEmail } from "utils/email";
import { clearSessionActivity } from "utils/sessionIdle";
import {
  clearCurrentBrowserSessionTracking,
  exchangeCognitoToken,
  getLinkedAccounts,
  isCurrentBrowserSessionTracked,
  rememberFeedbackReturnPath,
} from "services/accountSession";
import { LinkedAccountProfile } from "types/auth";

// Amplify is configured in config/amplifyAuth (called from index.tsx)

/**
 * @description Dispatch this thunk to send reset password request
 * @param {IResetPasswordRequestCredentials} reqData - Object containing email
 */
export const registerThunk = createAsyncThunk<
  ICreateUserResult,
  ICreateOrUpdateUserCredentials
>("auth/registerThunk", async (reqData, { rejectWithValue }) => {
  try {
    const email = normalizeEmail(reqData?.email?.toString() || "");
    const existingUserId = reqData?.user_id?.toString();
    const userId = await registerUser(
      email,
      reqData?.name?.toString() || "",
      reqData?.role as ROLES,
      existingUserId || undefined,
    );

    if (reqData?.sendInvite === true) {
      await resetPassword({
        username: email,
        options: {
          clientMetadata: {
            env: process.env.REACT_APP_ENV || "",
            origin: "register",
          },
        },
      });
    }

    return {
      status: "success",
      user_id: userId,
    };
  } catch (error) {
    console.log(error);
    return rejectWithValue(error);
  }
});

export const updateUserThunk = createAsyncThunk<
  string,
  ICreateOrUpdateUserCredentials
>("auth/updateUserThunk", async (reqData, { rejectWithValue }) => {
  try {
    const normalizedData = {
      ...reqData,
      ...(reqData.email
        ? { email: normalizeEmail(reqData.email.toString()) }
        : {}),
    };
    const { data } = await API.patch(
      API_ROUTES.USERS.UPDATE(reqData.user_id?.toString() || ""),
      normalizedData,
    );
    return data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

/**
 * @description Dispatch this thunk to delete user by id
 * @param {string} id - String id to find current user for deleting
 */
export const deleteUserThunk = createAsyncThunk(
  "users/deleteUserThunk",
  async (email: string, { rejectWithValue }) => {
    try {
      const { data } = await API.delete(API_ROUTES.USERS.DELETE(email));
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Something went wrong" },
      );
    }
  },
);

/**
 * @description Dispatch this thunk to send login data, and receive secret key for 2fa validation
 * @param {ILoginCredentials} reqData - Object containing email and password
 */
export const logInThunk = createAsyncThunk<ILoginResponse, ILoginCredentials>(
  "auth/logInThunk",
  async (reqData, { rejectWithValue }) => {
    try {
      const resp = await signIn({
        username: reqData?.username?.toString() || "",
        password: reqData?.password?.toString() || "",
      });

      if (resp.nextStep.signInStep !== LoginSteps.DONE) {
        return {
          type: LoginResponseType.ACTION_REQUIRED,
          payload: {
            action: resp.nextStep.signInStep,
            payload: { resp, email: reqData?.username?.toString() },
          },
        };
      }
      const session = await fetchAuthSession();
      const appSession = await exchangeAmplifySession(session);
      return {
        type: LoginResponseType.SUCCESS,
        payload: {
          token: appSession.token,
          user_id: session?.userSub || "",
        },
      };
    } catch (error: any) {
      await logout();
      console.log(error);
      console.log(error.message);
      return rejectWithValue(error);
    }
  },
);

export const setPasswordThunk = createAsyncThunk<
  string,
  IResetPasswordCredentials
>("auth/setPasswordThunk", async (reqData, { rejectWithValue }) => {
  try {
    if (reqData.code && reqData.username) {
      await confirmResetPassword({
        username: reqData?.username?.toString() || "",
        newPassword: reqData?.password?.toString() || "",
        confirmationCode: reqData?.code?.toString() || "",
      });
      return "not authenticated";
    } else {
      await confirmSignIn({
        challengeResponse: reqData.password?.toString() || "",
      });
    }
    const session = await fetchAuthSession();
    if (session?.tokens?.idToken) {
      await exchangeAmplifySession(session);
    }
    return "authenticated";
  } catch (error: any) {
    console.log(error);
    return rejectWithValue(error.message);
  }
});

/**
 * @description Dispatch this thunk to send reset password request
 * @param {IResetPasswordRequestCredentials} reqData - Object containing email
 */
export const resetPasswordRequestThunk = createAsyncThunk<
  void,
  IResetPasswordRequestCredentials
>("auth/resetPasswordRequestThunk", async (reqData, { rejectWithValue }) => {
  try {
    const options = {
      clientMetadata: { env: process.env.REACT_APP_ENV || "" },
    };
    if (reqData.isResendingInvite) {
      Object.assign(options.clientMetadata, { origin: "login-reminder" });
    }
    await resetPassword({
      username: reqData?.email?.toString() || "",
      options,
    });
  } catch (error) {
    console.log(error);
    return rejectWithValue(error);
  }
});

export const resetPasswordThunk = createAsyncThunk<
  string,
  IResetPasswordCredentials
>("auth/resetPasswordThunk", async (reqData, { rejectWithValue }) => {
  try {
    await confirmResetPassword({
      username: reqData?.username?.toString() || "",
      newPassword: reqData?.password?.toString() || "",
      confirmationCode: reqData?.code?.toString() || "",
    });
    return "success";
  } catch (error: any) {
    console.log(error);
    return rejectWithValue(error.message);
  }
});

/**
 * Decode JWT payload (middle part) without verification (client-side only).
 * Used to recover user from stored token when Amplify session is not restored after reload.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** True when localStorage holds a VetNebula app JWT (not a Cognito ID token). */
function isAppSessionToken(token: string | null): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  return (
    payload?.token_use === "vetnebula_session" &&
    !!payload?.["https://hasura.io/jwt/claims"]
  );
}

function getHasuraClaims(
  payload: Record<string, unknown>
): Record<string, unknown> | null {
  const rawClaims = payload["https://hasura.io/jwt/claims"];
  if (!rawClaims) {
    return null;
  }

  try {
    return typeof rawClaims === "string"
      ? (JSON.parse(rawClaims) as Record<string, unknown>)
      : (rawClaims as Record<string, unknown>);
  } catch {
    return null;
  }
}

function getUserIdFromIdTokenPayload(
  payload: Record<string, unknown>,
  attributes?: Record<string, unknown>
): string {
  const customUserId =
    (attributes?.["custom:id"] as string | undefined) ||
    (payload["custom:id"] as string | undefined);
  if (customUserId) {
    return customUserId;
  }

  const hasuraClaims = getHasuraClaims(payload);
  const hasuraUserId = hasuraClaims?.["x-hasura-user-id"] as string | undefined;
  if (hasuraUserId) {
    return hasuraUserId;
  }

  const cognitoUsername = payload["cognito:username"] as string | undefined;
  if (
    cognitoUsername &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      cognitoUsername
    )
  ) {
    return cognitoUsername;
  }

  return (payload.sub as string) || "";
}

/**
 * Build User from Cognito idToken payload (supports OAuth/federated users
 * where fetchUserAttributes may not include custom:role; role can come from
 * custom:role or from https://hasura.io/jwt/claims x-hasura-default-role).
 */
function userFromIdTokenPayload(payload: Record<string, unknown>): User {
  const user = {} as User;
  user.user_id = getUserIdFromIdTokenPayload(payload);
  user.email =
    (payload.email as string) || (payload["cognito:username"] as string) || "";
  const nameFromAddress = (payload.address as { formatted?: string })
    ?.formatted;
  user.name = (payload.name as string) || nameFromAddress || user.email || "";
  let role = payload["custom:role"] as string | undefined;
  if (!role) {
    role = payload.role as string | undefined;
  }
  if (!role) {
    const hasuraClaims = getHasuraClaims(payload);
    role = hasuraClaims?.["x-hasura-default-role"] as string | undefined;
  }
  user.role = normalizeAppRole(role);
  user.org_id = (payload.org_id as string) || undefined;
  user.tags = [];
  return user;
}

function normalizeAppRole(role?: string | null): ROLES {
  const normalized = (role || "").trim().toLowerCase();
  if (normalized === "sudo") return ROLES.ADMIN;
  if (Object.values(ROLES).includes(normalized as ROLES)) {
    return normalized as ROLES;
  }
  return ROLES.STUDENT;
}

function userFromStoredAppToken(): User | null {
  const appToken = LocalStorage.getToken();
  if (!isAppSessionToken(appToken) || !appToken) return null;
  const appPayload = decodeJwtPayload(appToken);
  if (!appPayload?.["https://hasura.io/jwt/claims"]) return null;
  const tokenUser = userFromIdTokenPayload(appPayload);
  return tokenUser.user_id ? tokenUser : null;
}

/** True when localStorage still holds an app JWT that has not expired. */
export function appSessionStillUsable(): boolean {
  const token = LocalStorage.getToken();
  if (!isAppSessionToken(token) || !token) return false;
  const payload = decodeJwtPayload(token);
  const exp = typeof payload?.exp === "number" ? payload.exp : 0;
  return exp === 0 || exp * 1000 > Date.now();
}

function userFromLinkedProfile(profile: LinkedAccountProfile): User {
  return {
    user_id: profile.user_id,
    name: profile.name,
    email: profile.email,
    role: normalizeAppRole(profile.role),
    picture_url: profile.picture_url ?? undefined,
    org_id: profile.org_id ?? undefined,
    tags: [],
  };
}

async function exchangeAmplifySession(
  session: Awaited<ReturnType<typeof fetchAuthSession>>,
) {
  const idToken = session.tokens?.idToken;
  if (!idToken) {
    throw new Error("Cognito session does not include an ID token.");
  }
  LocalStorage.setCognitoTokenExpirationTime(
    idToken.payload?.exp?.toString() || "0",
  );
  return exchangeCognitoToken(idToken.toString());
}

/**
 * @description Dispatch this thunk to send sign in data if existent
 */
export const checkLoggedUserThunk = createAsyncThunk<User, void>(
  "auth/checkLoggedUserThunk",
  async (reqData, { rejectWithValue }) => {
    try {
      const session = await fetchAuthSession();
      const hasCognitoSession = !!session?.tokens?.idToken;
      if (!hasCognitoSession && !isAppSessionToken(LocalStorage.getToken())) {
        await logout();
        return rejectWithValue("User not authenticated");
      }

      if (
        hasCognitoSession &&
        (!isAppSessionToken(LocalStorage.getToken()) ||
          !isCurrentBrowserSessionTracked())
      ) {
        try {
          await exchangeAmplifySession(session);
        } catch (exchangeError) {
          // A new tab always re-exchanges because its sessionStorage flag is
          // empty. If that call fails (linked-account conflict, staging API),
          // keep the app token already in localStorage. Logging out here sends
          // the feedback page to login, and the login screen then drops a
          // still-valid session on Home.
          if (!isAppSessionToken(LocalStorage.getToken())) {
            throw exchangeError;
          }
        }
      }

      try {
        const linked = await getLinkedAccounts();
        const active =
          linked.active ||
          linked.accounts.find(
            (account) =>
              account.current || account.user_id === linked.active_user_id,
          ) ||
          (linked.accounts.length === 1 ? linked.accounts[0] : undefined);
        if (active) {
          amplitude.setUserId(active.user_id);
          return userFromLinkedProfile(active);
        }
      } catch (profileError) {
        const tokenUser = userFromStoredAppToken();
        if (tokenUser) {
          amplitude.setUserId(tokenUser.user_id);
          return tokenUser;
        }
        throw profileError;
      }

      const tokenUser = userFromStoredAppToken();
      if (tokenUser) {
        amplitude.setUserId(tokenUser.user_id);
        return tokenUser;
      }
      throw new Error("Active VetNebula profile could not be resolved.");
    } catch (error) {
      const tokenUser = userFromStoredAppToken();
      if (tokenUser) {
        amplitude.setUserId(tokenUser.user_id);
        return tokenUser;
      }
      await logout();
      return rejectWithValue(error);
    }
  },
);

export const changePasswordThunk = createAsyncThunk<string, IChangePassword>(
  "auth/changePasswordThunk",
  async (reqData, { rejectWithValue }) => {
    try {
      await updatePassword({
        oldPassword: reqData?.password?.toString() || "",
        newPassword: reqData?.newPassword?.toString() || "",
      });
      return "success";
    } catch (error: any) {
      return rejectWithValue(error.response.data.error);
    }
  },
);

export const loginCodeThunk = createAsyncThunk<string, ICodeLoginResponse>(
  "auth/loginCodeThunk",
  async (reqData, { rejectWithValue }) => {
    try {
      const { nextStep } = await signIn({
        username: reqData.email?.toString() || "",
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
        },
      });
      console.log("next step:", nextStep);
      return nextStep.toString();
    } catch (error: any) {
      return rejectWithValue(error.response.data.error);
    }
  },
);

export const confirmLoginCodeThunk = createAsyncThunk<
  string,
  ICodeLoginResponse
>("auth/confirmLoginCodeThunk", async (reqData, { rejectWithValue }) => {
  try {
    console.log("code: ", reqData.code?.toString());
    const output = await confirmSignIn({
      challengeResponse: reqData.code?.toString() || "",
    });
    if (output?.isSignedIn !== true) {
      return rejectWithValue("Error confirming login code");
    }
    console.log(output);
    const session = await fetchAuthSession();
    await exchangeAmplifySession(session);
    console.log();
    return "";
  } catch (error: any) {
    return rejectWithValue(error.response.data.error);
  }
});

export const selectMfaFactorThunk = createAsyncThunk<
  ILoginResponse,
  "TOTP" | "SMS" | "EMAIL"
>("auth/selectMfaFactorThunk", async (factor, { rejectWithValue }) => {
  try {
    const resp = await confirmSignIn({ challengeResponse: factor });
    if (resp.nextStep.signInStep !== LoginSteps.DONE) {
      return {
        type: LoginResponseType.ACTION_REQUIRED,
        payload: {
          action: resp.nextStep.signInStep,
          payload: { resp },
        },
      };
    }
    const session = await fetchAuthSession();
    const appSession = await exchangeAmplifySession(session);
    return {
      type: LoginResponseType.SUCCESS,
      payload: {
        token: appSession.token,
        user_id: session?.userSub || "",
      },
    };
  } catch (error: any) {
    console.error("selectMfaFactorThunk error:", error);
    return rejectWithValue(error?.message || "Could not select MFA method");
  }
});

export const confirmMfaThunk = createAsyncThunk<ILoginResponse, { code: string }>(
  "auth/confirmMfaThunk",
  async ({ code }, { rejectWithValue }) => {
    try {
      const resp = await confirmSignIn({
        challengeResponse: code.toString().trim(),
      });
      if (resp.nextStep.signInStep !== LoginSteps.DONE) {
        return {
          type: LoginResponseType.ACTION_REQUIRED,
          payload: {
            action: resp.nextStep.signInStep,
            payload: { resp },
          },
        };
      }
      const session = await fetchAuthSession();
      const appSession = await exchangeAmplifySession(session);
      return {
        type: LoginResponseType.SUCCESS,
        payload: {
          token: appSession.token,
          user_id: session?.userSub || "",
        },
      };
    } catch (error: any) {
      console.error("confirmMfaThunk error:", error);
      return rejectWithValue(error?.message || "MFA verification failed");
    }
  },
);

export const beginTotpSetupThunk = createAsyncThunk<
  { sharedSecret: string; setupUri: string },
  void
>("auth/beginTotpSetupThunk", async (_, { rejectWithValue }) => {
  try {
    const details = await setUpTOTP();
    const setupUri = details.getSetupUri("VetNebula").toString();
    return {
      sharedSecret: details.sharedSecret,
      setupUri,
    };
  } catch (error: any) {
    console.error("beginTotpSetupThunk error:", error);
    return rejectWithValue(error?.message || "Could not start MFA setup");
  }
});

export const verifyTotpSetupThunk = createAsyncThunk<
  ILoginResponse | { preferred: true },
  { code: string; completeSignIn?: boolean }
>(
  "auth/verifyTotpSetupThunk",
  async ({ code, completeSignIn = true }, { rejectWithValue }) => {
    try {
      const trimmed = code.toString().trim();
      await verifyTOTPSetup({ code: trimmed });
      await updateMFAPreference({ totp: "PREFERRED" });

      if (!completeSignIn) {
        return { preferred: true };
      }

      const resp = await confirmSignIn({ challengeResponse: trimmed });
      if (resp.nextStep.signInStep !== LoginSteps.DONE) {
        return {
          type: LoginResponseType.ACTION_REQUIRED,
          payload: {
            action: resp.nextStep.signInStep,
            payload: { resp },
          },
        };
      }
      const session = await fetchAuthSession();
      const appSession = await exchangeAmplifySession(session);
      return {
        type: LoginResponseType.SUCCESS,
        payload: {
          token: appSession.token,
          user_id: session?.userSub || "",
        },
      };
    } catch (error: any) {
      console.error("verifyTotpSetupThunk error:", error);
      return rejectWithValue(error?.message || "MFA setup verification failed");
    }
  },
);

export type MfaPreferenceStatus = {
  enabled: boolean;
  preferred?: string;
};

export const fetchMfaPreferenceThunk = createAsyncThunk<
  MfaPreferenceStatus,
  void
>("auth/fetchMfaPreferenceThunk", async (_, { rejectWithValue }) => {
  try {
    const preference = await fetchMFAPreference();
    const enabled = Boolean(
      preference.enabled?.includes("TOTP") ||
        preference.enabled?.includes("SMS") ||
        preference.enabled?.includes("EMAIL") ||
        preference.preferred,
    );
    return {
      enabled,
      preferred: preference.preferred,
    };
  } catch (error: any) {
    // Federated / SAML users may not support software MFA on the Cognito user.
    console.error("fetchMfaPreferenceThunk error:", error);
    return rejectWithValue(error?.message || "Could not read MFA preference");
  }
});

export const logoutThunk = createAsyncThunk<void, void>(
  "auth/logoutThunk",
  async (_, { rejectWithValue }) => {
    try {
      await logout();
    } catch (error: any) {
      return rejectWithValue(error.response.data.error);
    }
  },
);

export const registerUser = async (
  username: string,
  name: string,
  role: ROLES,
  existingUserId?: string,
): Promise<string> => {
  const userId = existingUserId || crypto.randomUUID();

  try {
    const password = generateTemporaryPassword();
    await signUp({
      username,
      password,
      options: {
        userAttributes: {
          name,
          "custom:role": role,
          "custom:id": userId,
        },
      },
    });
  } catch (e: any) {
    if (!e.message.includes("PREVENT_SIGNUP_MESSAGE")) {
      throw e;
    }
  }

  return userId;
};

export const logout = async () => {
  try {
    await signOut({ global: true });
  } catch (error: any) {
    console.log(error);
  } finally {
    LocalStorage.clearToken();
    clearCurrentBrowserSessionTracking();
    clearSessionActivity();
  }
};

let isHandlingSessionExpiry = false;

/**
 * Clears the session and sends the browser to the login page once.
 * Concurrent callers are ignored while logout/redirect is in flight.
 */
export const handleSessionExpired = async (): Promise<void> => {
  if (isHandlingSessionExpiry) return;
  isHandlingSessionExpiry = true;
  try {
    rememberFeedbackReturnPath();
    await logout();
    if (window.location.pathname !== "/") {
      window.location.assign("/");
    }
  } finally {
    isHandlingSessionExpiry = false;
  }
};

export const refreshTokenIfNeeded = async (force = false): Promise<boolean> => {
  try {
    const token = LocalStorage.getToken();
    const payload = token ? decodeJwtPayload(token) : null;
    const jwtExp = typeof payload?.exp === "number" ? payload.exp : 0;
    const storedExp = Number(LocalStorage.getTokenExpirationTime());
    // A missing expiration is stored as "0". Treating that as already expired
    // force-refreshes every GraphQL call. The feedback detail query is
    // network-only, so that refresh runs when the page opens. If the exchange
    // fails, the old code signed the user out and the login screen sent them
    // Home. Prefer the app JWT exp, and skip the refresh while it is valid.
    const exp = storedExp > 0 ? storedExp : jwtExp;
    const needsAppToken = !isAppSessionToken(token);
    const nearExpiry =
      exp > 0 && Date.now() > exp * 1000 - 5 * 60 * 1000;
    if (force || needsAppToken || nearExpiry) {
      const session = await fetchAuthSession({ forceRefresh: true });
      await exchangeAmplifySession(session);
      console.log(`REFRESHING TOKEN NOW At: ${new Date().toISOString()}`);
    }
    return true;
  } catch (e) {
    console.log(e);
    if (appSessionStillUsable()) {
      return force ? false : true;
    }
    await handleSessionExpired();
    return false;
  }
};
